import React, { useState, useRef, useEffect } from 'react';

const SearchableDropdown = ({ 
  options = [], 
  value, 
  onChange, 
  placeholder = "Search and select...", 
  searchPlaceholder = "Search...",
  className = "",
  disabled = false,
  renderOption = null,
  renderSelected = null,
  getOptionValue = (option) => option.value,
  getOptionLabel = (option) => option.label,
  getSearchText = (option) => option.label,
  emptyMessage = "No options found"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  const filteredOptions = options.filter(option => {
    const searchText = getSearchText(option);
    if (!searchText) return false;
    
    // Clean up the search text by removing extra spaces and normalizing
    const cleanSearchText = searchText.toLowerCase().replace(/\s+/g, ' ').trim();
    const cleanSearchTerm = searchTerm.toLowerCase().trim();
    
    // Support partial matching for email domains (e.g., searching "@gmail" should match "user@gmail.com")
    return cleanSearchText.includes(cleanSearchTerm);
  });

  // Find selected option
  const selectedOption = options.find(option => getOptionValue(option) === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  const handleToggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm('');
      }
    }
  };

  const handleOptionSelect = (option) => {
    onChange(getOptionValue(option));
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClearSelection = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    } else if (e.key === 'Enter' && filteredOptions.length === 1) {
      handleOptionSelect(filteredOptions[0]);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Main dropdown trigger */}
      <div
        onClick={handleToggleDropdown}
        className={`w-full px-3 py-2 border rounded-md bg-white cursor-pointer transition-colors flex items-center justify-between ${
          disabled 
            ? 'bg-gray-100 cursor-not-allowed border-gray-200' 
            : isOpen
            ? 'border-blue-500 ring-1 ring-blue-500'
            : 'border-gray-300 hover:border-gray-400'
        } focus:ring-blue-500 focus:border-blue-500 shadow-sm sm:text-sm`}
      >
        <div className="flex-1 min-w-0">
          {selectedOption ? (
            renderSelected ? renderSelected(selectedOption) : (
              <span className="text-gray-900">
                {getOptionLabel(selectedOption)}
              </span>
            )
          ) : (
            <span className="text-gray-500">{placeholder}</span>
          )}
        </div>
        
        <div className="flex items-center space-x-1 ml-2">
          {selectedOption && !disabled && (
            <button
              onClick={handleClearSelection}
              className="p-1 hover:bg-gray-100 rounded-full transition-colors"
              type="button"
            >
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg 
            className={`w-4 h-4 text-gray-400 transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search input */}
          <div className="p-3 border-b border-gray-100">
            <div className="relative">
              <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                ref={searchInputRef}
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
              />
            </div>
          </div>

          {/* Options list */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option, index) => (
                <div
                  key={getOptionValue(option)}
                  onClick={() => handleOptionSelect(option)}
                  className={`px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
                    getOptionValue(option) === value ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                  }`}
                >
                  {renderOption ? renderOption(option) : getOptionLabel(option)}
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                {emptyMessage}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableDropdown;
